/**
 * Goal value formatting (Story 18.3 contract — `formatGoalValue`).
 *
 * NOTE (Story 18.8 sequencing): 18.3 had NOT landed when 18.8 was
 * implemented (it was ready-for-dev with no artifacts on disk), so this
 * shared formatter was created HERE under the same tolerate-if-missing rule
 * that produced services/goal-progress.ts (its docblock: "created by Story
 * 18.9 under its tolerate-if-missing rule — 18.3 extends this exact module").
 * 18.3 OWNS this module and may extend it; the Goals page + dialog consume
 * `formatGoalValue` verbatim. The logic mirrors the private formatter in
 * GoalWidget.tsx exactly (currency = no decimals; percentage = 1 decimal;
 * number = grouped, no decimals) so the page and the report widget never
 * disagree on a value's rendering.
 */

import type { GoalUnitType, GoalPeriodType } from "./goalPageDisplay";

/**
 * Formats a goal's numeric value per its unit. Percentage shows one decimal;
 * currency and number show none (demo-fidelity, mirrors GoalWidget).
 */
export function formatGoalValue(
  value: number,
  unit: GoalUnitType,
  currencyCode = "USD",
): string {
  if (unit === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (unit === "percentage") {
    return `${value.toFixed(1)}%`;
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

/** Clamps a percentage to [0, 100] for the progress-bar FILL only — the
 * displayed percentage label may exceed 100 (the 18.3 "UI clamps the bar,
 * not the number" rule). */
export function clampBarFill(percentage: number): number {
  if (Number.isNaN(percentage)) return 0;
  return Math.max(0, Math.min(100, percentage));
}

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/**
 * Human period label for the history table (Story 18.7, AC #6a). Anchored to
 * the goal's `periodEnd` in UTC (the bucketing axis), formatted by period type:
 * - monthly   → "Apr 2026"
 * - quarterly → "Q2 2026"
 * - yearly    → "2026"
 * - custom    → explicit "Feb 1 – May 15, 2026" range (falls back to the
 *               end-month label when periodStart is missing)
 *
 * Returns "—" when periodEnd is null (a goal with no period end is not "past"
 * and should never reach the history table, but the formatter is total).
 */
export function formatGoalPeriodLabel(
  periodType: GoalPeriodType,
  periodStart: number | null,
  periodEnd: number | null,
): string {
  if (periodEnd === null) return "—";
  const end = new Date(periodEnd);
  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth(); // 0-based

  if (periodType === "yearly") return String(endYear);
  if (periodType === "quarterly") {
    const quarter = Math.floor(endMonth / 3) + 1;
    return `Q${quarter} ${endYear}`;
  }
  if (periodType === "custom") {
    if (periodStart === null) return `${SHORT_MONTHS[endMonth]} ${endYear}`;
    const start = new Date(periodStart);
    const startLabel = `${SHORT_MONTHS[start.getUTCMonth()]} ${start.getUTCDate()}`;
    const endLabel = `${SHORT_MONTHS[endMonth]} ${end.getUTCDate()}`;
    // Include the year once unless the range spans two years.
    const startYear = start.getUTCFullYear();
    return startYear === endYear
      ? `${startLabel} – ${endLabel}, ${endYear}`
      : `${startLabel}, ${startYear} – ${endLabel}, ${endYear}`;
  }
  // monthly (default)
  return `${SHORT_MONTHS[endMonth]} ${endYear}`;
}
